import React, {memo} from 'react';
import {useTranslation} from 'react-i18next';
import {
   IUserActivityReportParameters,
   UserActivityReportParameters,
} from 'types/diplomat/reports/UserActivityReportParameters';
import Panel from 'components/Panel';
import {Box, Grid} from '@mui/material';
import FormikRadioButton from 'components/inputs/FormikRadioButton';
import FormikCheckbox from 'components/inputs/FormikCheckbox';
import FormikTextField from 'components/inputs/FormikTextField';
import {FormFooterButtons} from 'components/buttons/FormFooterButtons';
import FormikDropdown from 'components/inputs/FormikDropdown';
import {DiplomatObjectType} from 'types/DiplomatObjectType';
import Formik, {FormOnSubmitHelpers} from 'components/hook-form/Formik';
import {useFieldValueGetter} from 'components/hook-form/useFieldValueGetter';
import {enums} from 'types/enums';
import FormikSwitchValuable from 'components/inputs/FormikSwitchValueable';
import {ByDateReportTypeFormPart} from 'features/layout/DialogRoutes/dialogs/reports/parts/ByDateReportTypeFormPart';

type Props = {
   onClose: () => void;
   values?: IUserActivityReportParameters;
   onSubmit: (
      values: IUserActivityReportParameters,
      bag: FormOnSubmitHelpers<IUserActivityReportParameters>
   ) => void;
};
export const UserActivityReportForm = memo(({onClose, values, onSubmit}: Props) => {
   const params = values || {
      allRecords: true,
   };

   return (
      <Formik
         initialValues={UserActivityReportParameters.cast(params)}
         validationSchema={UserActivityReportParameters}
         onSubmit={onSubmit}
         initialTouched={['allRecords']}
      >
         <UserActivityReport onClose={onClose} />
      </Formik>
   );
});

function UserActivityReport({onClose}: {onClose: () => void}) {
   const {t} = useTranslation();
   const getValue = useFieldValueGetter<IUserActivityReportParameters>();
   const enabled = !getValue('allRecords');
   const byUserId = getValue('byUserId');
   const byUserIp = getValue('byUserIp');
   const byObjectType = getValue('byObjectType');
   const byObjectId = getValue('byObjectId');

   return (
      <>
         <Panel>
            <Grid container spacing={3}>
               <Grid item sm={12}>
                  <FormikRadioButton
                     name="allRecords"
                     key="allRecordsTrue"
                     value={true}
                     label={t('Select All Records')}
                  />
               </Grid>
            </Grid>
            <Grid container spacing={3}>
               <Grid item sm={12}>
                  <FormikRadioButton
                     name="allRecords"
                     key="allRecordsFalse"
                     value={false}
                     label={t('Select Records')}
                  />
               </Grid>
            </Grid>
            <Box marginLeft={8}>
               <Grid container spacing={3}>
                  <Grid item sm={3}>
                     <FormikCheckbox
                        name="byUserId"
                        label={t('By User ID containing:')}
                        disabled={!enabled}
                     />
                  </Grid>
                  <Grid item sm={8}>
                     <FormikTextField
                        name="userId"
                        disabled={!enabled || !byUserId}
                        // height="230px"
                     />
                  </Grid>
               </Grid>
               <Grid container spacing={3}>
                  <Grid item sm={3}>
                     <FormikCheckbox
                        name="byUserIp"
                        label={t('By User IP Address containing:')}
                        disabled={!enabled}
                     />
                  </Grid>
                  <Grid item sm={8}>
                     <FormikTextField
                        name="userIp"
                        disabled={!enabled || !byUserIp}
                        // height="230px"
                     />
                  </Grid>
               </Grid>
               <Grid container spacing={3}>
                  <Grid item sm={3}>
                     <FormikCheckbox
                        name="byObjectType"
                        label={t('By Object Type:')}
                        disabled={!enabled}
                     />
                  </Grid>
                  <Grid item sm={8}>
                     <FormikDropdown
                        name="objectType"
                        disabled={!enabled || !byObjectType}
                        options={DiplomatObjectType.options}
                        // height="230px"
                     />
                  </Grid>
               </Grid>

               <Grid container spacing={3}>
                  <Grid item sm={3}>
                     <FormikCheckbox
                        name="byObjectId"
                        label={t('By Object ID containing:')}
                        disabled={!enabled}
                     />
                  </Grid>
                  <Grid item sm={8}>
                     <FormikTextField
                        name="objectId"
                        disabled={!enabled || !byObjectId}
                        // height="230px"
                     />
                  </Grid>
               </Grid>
               <Grid container spacing={3}>
                  <ByDateReportTypeFormPart />
               </Grid>
            </Box>

            <Grid container spacing={3}>
               <Grid item sm={12}>
                  <FormikCheckbox name="includeArchive" label={t('Include Archive Records')} />
               </Grid>
            </Grid>
         </Panel>

         <Box
            sx={{
               margin: '0 auto',
               width: '18rem',
            }}
         >
            <FormikSwitchValuable
               name="_format"
               options={[enums.reportFormats.options[0], enums.reportFormats.options[1]]}
            />
         </Box>
         <FormFooterButtons cancel={onClose} />
         {/* {JSON.stringify(values)} */}
      </>
   );
}
